import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { disablePushNotifications, enablePushNotifications, getPushStatus, PUSH_SUPPORTED } from "@/lib/pushNotifications";

export default function NotificationSettings() {
  // status: "unsupported" | "denied" | "disabled" | "enabled"
  const [status, setStatus] = useState(PUSH_SUPPORTED ? "disabled" : "unsupported");
  const [busy, setBusy] = useState(false);
  // Sound is a purely local preference: no server state, no push subscription.
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("vs_sound") !== "off");

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      localStorage.setItem("vs_sound", next ? "on" : "off");
      return next;
    });
  };

  useEffect(() => {
    if (!PUSH_SUPPORTED) return;
    let alive = true;
    getPushStatus()
      .then((next) => { if (alive) setStatus(next); })
      .catch(() => { /* leave as disabled */ });
    return () => { alive = false; };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const next = await enablePushNotifications();
      setStatus(next);
      if (next === "enabled") toast.success("Notificări activate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notificările nu au putut fi activate");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disablePushNotifications();
      setStatus("disabled");
      toast.success("Notificări dezactivate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notificările nu au putut fi dezactivate");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="notification-settings">
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          {status === "enabled" ? <BellRing className="w-4 h-4 text-brand" /> : <Bell className="w-4 h-4 text-fg/50" />}
          <h3 className="font-bold tracking-tight">Notificări</h3>
        </div>
        <p className="text-xs text-fg/50 leading-relaxed mb-4">
          Primești o notificare în browser când cineva <span className="text-brand">te menționează</span> sau îți trimite un mesaj direct — chiar și cu tabul închis.
        </p>

        {status === "unsupported" && (
          <div className="flex items-center gap-2 text-xs text-fg/50">
            <BellOff className="w-3.5 h-3.5" /> Browserul ăsta nu acceptă notificări push.
          </div>
        )}

        {status === "denied" && (
          <div className="flex items-center gap-2 text-xs text-destructive">
            <BellOff className="w-3.5 h-3.5" /> Notificările sunt blocate. Permite-le pentru acest site din setările browserului, apoi reîncarcă pagina.
          </div>
        )}

        {status === "disabled" && (
          <button
            data-testid="enable-notifications"
            onClick={enable}
            disabled={busy}
            className="btn-primary text-sm flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50"
          >
            <Bell className="w-3.5 h-3.5" /> {busy ? "Enabling…" : "Activează notificările"}
          </button>
        )}

        {status === "enabled" && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-brand flex items-center gap-1.5"><BellRing className="w-3.5 h-3.5" /> Activate în acest browser</span>
            <button
              data-testid="disable-notifications"
              onClick={disable}
              disabled={busy}
              className="text-sm flex items-center gap-1.5 px-3 py-1.5 border border-line/20 text-fg/70 hover:border-line/40 disabled:opacity-50"
            >
              <BellOff className="w-3.5 h-3.5" /> {busy ? "Disabling…" : "Disable"}
            </button>
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-line/10 flex items-center gap-3">
          {soundOn ? <Volume2 className="w-4 h-4 text-brand" /> : <VolumeX className="w-4 h-4 text-fg/50" />}
          <div className="min-w-0">
            <p className="text-sm font-medium">Sunet notificări</p>
            <p className="text-xs text-fg/50">Un ton scurt la mesaj nou, când nu ești pe conversația respectivă.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={soundOn}
            aria-label="Sunet notificări"
            data-testid="toggle-notification-sound"
            onClick={toggleSound}
            className={`ml-auto flex-shrink-0 w-10 h-5 border transition-colors ${soundOn ? "bg-brand/30 border-brand/50" : "bg-surface-2 border-line/20"}`}
          >
            <span className={`block w-4 h-4 mt-px transition-transform ${soundOn ? "translate-x-5 bg-brand" : "translate-x-px bg-fg/40"}`} />
          </button>
        </div>
      </div>
    </section>
  );
}
